<script setup>
import {
  CsTable,
  CsTableBody,
  CsTableCell,
  CsTableContainer,
  CsTableHead,
  CsTableHeader,
  CsTableRow,
} from '../table';
import { CsBadge } from '../badge';
import { CsTag } from '../tag';
import { nextTick, ref } from 'vue';

const props = defineProps({
  data: {
    type: [Object, Array],
    required: true,
  },
});

const expandedTexts = ref(new Set());
const truncatedTexts = ref(new Set());

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isArray(value) {
  return Array.isArray(value);
}

function isPrimitive(value) {
  return value === null || typeof value !== 'object';
}

function isArrayOfObjects(value) {
  return (
    isArray(value) && value.length > 0 && value.every(item => isObject(item))
  );
}

function isArrayOfPrimitives(value) {
  return (
    isArray(value) && value.length > 0 && value.every(item => isPrimitive(item))
  );
}

function isArrayOfStrings(value) {
  return (
    isArray(value) &&
    value.length > 0 &&
    value.every(item => typeof item === 'string')
  );
}

function isBoolean(value) {
  return typeof value === 'boolean';
}

function isNumber(value) {
  return typeof value === 'number' && !Number.isNaN(value);
}

function isDate(value) {
  if (typeof value !== 'string') return false;
  // ISO 8601 date format or common date patterns
  const isoPattern =
    /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(?:Z|[+-]\d{2}:\d{2})?)?$/;
  const timestamp = Date.parse(value);
  return isoPattern.test(value) && !Number.isNaN(timestamp);
}

function isUrl(value) {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isEmail(value) {
  if (typeof value !== 'string') return false;
  const emailPattern = /^[\w.-]+@[\w.-]+\.\w{2,}$/;
  return emailPattern.test(value);
}

function isImageUrl(value) {
  if (!isUrl(value)) return false;
  const imageExtensions = /\.(?:jpg|jpeg|png|gif|webp|svg|bmp|ico)(?:\?.*)?$/i;
  return imageExtensions.test(value);
}

function isLongText(value) {
  return typeof value === 'string' && value.length > 100;
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatDate(value) {
  const date = new Date(value);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function truncateUrl(url, maxLength = 50) {
  if (url.length <= maxLength) return url;
  return `${url.substring(0, maxLength)}...`;
}

function formatPrimitive(value) {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value.toString();
  if (typeof value === 'number') return formatNumber(value);
  if (typeof value === 'string') return value;
  return String(value);
}

function getTableHeaders(arrayOfObjects) {
  if (!arrayOfObjects || arrayOfObjects.length === 0) return [];

  // Get all unique keys from all objects
  const keysSet = new Set();
  arrayOfObjects.forEach(obj => {
    Object.keys(obj).forEach(key => keysSet.add(key));
  });

  return Array.from(keysSet);
}

function toggleTextExpansion(key, nestedKey = null) {
  const id = nestedKey ? `${key}-${nestedKey}` : key;
  if (expandedTexts.value.has(id)) {
    expandedTexts.value.delete(id);
  } else {
    expandedTexts.value.add(id);
  }
}

function isTextExpanded(key, nestedKey = null) {
  const id = nestedKey ? `${key}-${nestedKey}` : key;
  return expandedTexts.value.has(id);
}

function checkIfTruncated(element, id) {
  if (!element) return;

  nextTick(() => {
    // Check if element is truncated by comparing scrollHeight with clientHeight
    const isTruncated = element.scrollHeight > element.clientHeight;

    if (isTruncated) {
      truncatedTexts.value.add(id);
    } else {
      truncatedTexts.value.delete(id);
    }
  });
}

function isTextTruncated(key, nestedKey = null) {
  const id = nestedKey ? `${key}-${nestedKey}` : key;
  return truncatedTexts.value.has(id);
}

function toSentenceCase(str) {
  // Convert snake_case or camelCase to sentence case
  const words = str
    // Handle snake_case: replace underscores with spaces
    .replace(/_/g, ' ')
    // Handle camelCase: insert space before capital letters
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Handle acronyms: insert space before capital letter followed by lowercase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .toLowerCase();

  // Capitalize the first letter
  return words.charAt(0).toUpperCase() + words.slice(1);
}
</script>

<template>
  <CsTableContainer
    data-id="table-root"
    class="w-full border-t border-brand-border overflow-auto"
  >
    <div class="relative flex w-full">
      <CsTable class="w-full">
        <CsTableBody>
          <CsTableRow v-for="(value, key) in props.data" :key="key">
            <CsTableCell
              width="30%"
              class="border-r border-brand-border bg-brand-surface-secondary !align-top font-medium"
            >
              {{ toSentenceCase(key) }}
            </CsTableCell>

            <CsTableCell class="!align-top">
              <!-- Boolean value with CsBadge -->
              <div v-if="isBoolean(value)">
                <CsBadge :variant="value ? 'success' : 'critical'">
                  {{ value ? 'Yes' : 'No' }}
                </CsBadge>
              </div>

              <!-- Number value with thousands separator -->
              <div v-else-if="isNumber(value)">
                {{ formatNumber(value) }}
              </div>

              <!-- Image URL with preview -->
              <div
                v-else-if="isImageUrl(value)"
                class="flex items-center gap-2"
              >
                <img
                  :src="value"
                  :alt="value"
                  class="h-12 w-12 rounded border object-cover"
                />
                <a
                  :href="value"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-blue-600 hover:underline"
                >
                  {{ truncateUrl(value, 40) }}
                </a>
              </div>

              <!-- Regular URL (clickable link) -->
              <div v-else-if="isUrl(value)">
                <a
                  :href="value"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-600 hover:underline"
                >
                  {{ truncateUrl(value, 50) }}
                </a>
              </div>

              <!-- Email (mailto link) -->
              <div v-else-if="isEmail(value)">
                <a
                  :href="`mailto:${value}`"
                  class="text-blue-600 hover:underline"
                >
                  {{ value }}
                </a>
              </div>

              <!-- Date/Time formatted -->
              <div v-else-if="isDate(value)">
                <span class="text-sm">{{ formatDate(value) }}</span>
              </div>

              <!-- Long text with line clamp and load more button -->
              <div v-else-if="isLongText(value)">
                <p
                  :ref="el => checkIfTruncated(el, key)"
                  class="text-sm"
                  :class="{ 'line-clamp-3': !isTextExpanded(key) }"
                >
                  {{ value }}
                </p>
                <button
                  v-if="isTextTruncated(key) || isTextExpanded(key)"
                  type="button"
                  class="mt-1 text-xs text-blue-600 hover:underline"
                  @click="toggleTextExpansion(key)"
                >
                  {{ isTextExpanded(key) ? 'Show less' : 'Load more' }}
                </button>
              </div>

              <!-- Array of strings (show as CsTag) -->
              <div
                v-else-if="isArrayOfStrings(value)"
                class="flex flex-wrap gap-1"
              >
                <CsTag
                  v-for="(item, index) in value"
                  :key="index"
                  :is-closable="false"
                >
                  {{ item }}
                </CsTag>
              </div>

              <!-- Array of primitives (show as comma-separated) -->
              <div v-else-if="isArrayOfPrimitives(value)">
                {{ value.join(', ') }}
              </div>

              <!-- Primitive value (string, null, etc) -->
              <div v-else-if="isPrimitive(value)">
                {{ formatPrimitive(value) }}
              </div>

              <!-- Array of objects (render as nested table) -->
              <div v-else-if="isArrayOfObjects(value)">
                <CsTableContainer
                  data-id="table-child"
                  class="w-full border border-brand-border overflow-auto"
                >
                  <div class="relative flex w-full">
                    <CsTable class="w-full">
                      <CsTableHeader data-id="table-header">
                        <CsTableHead
                          v-for="header in getTableHeaders(value)"
                          :key="header"
                          class="group"
                        >
                          {{ toSentenceCase(header) }}
                        </CsTableHead>
                      </CsTableHeader>

                      <CsTableBody>
                        <CsTableRow v-for="(item, index) in value" :key="index">
                          <CsTableCell
                            v-for="header in getTableHeaders(value)"
                            :key="header"
                            class="!align-middle"
                          >
                            <!-- Nested boolean with CsBadge -->
                            <div v-if="isBoolean(item[header])">
                              <CsBadge
                                :variant="item[header] ? 'success' : 'critical'"
                              >
                                {{ item[header] ? 'Yes' : 'No' }}
                              </CsBadge>
                            </div>

                            <!-- Nested number with thousands separator -->
                            <div v-else-if="isNumber(item[header])">
                              {{ formatNumber(item[header]) }}
                            </div>

                            <!-- Nested image URL with preview -->
                            <div
                              v-else-if="isImageUrl(item[header])"
                              class="flex items-center gap-2"
                            >
                              <img
                                :src="item[header]"
                                :alt="item[header]"
                                class="h-12 w-12 rounded border object-cover"
                              />
                              <a
                                :href="item[header]"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="text-sm text-blue-600 hover:underline"
                              >
                                {{ truncateUrl(item[header], 40) }}
                              </a>
                            </div>

                            <!-- Nested URL (clickable link) -->
                            <div v-else-if="isUrl(item[header])">
                              <a
                                :href="item[header]"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="text-blue-600 hover:underline"
                              >
                                {{ truncateUrl(item[header], 50) }}
                              </a>
                            </div>

                            <!-- Nested email (mailto link) -->
                            <div v-else-if="isEmail(item[header])">
                              <a
                                :href="`mailto:${item[header]}`"
                                class="text-blue-600 hover:underline"
                              >
                                {{ item[header] }}
                              </a>
                            </div>

                            <!-- Nested date/time formatted -->
                            <div v-else-if="isDate(item[header])">
                              <span class="text-sm">{{
                                formatDate(item[header])
                              }}</span>
                            </div>

                            <!-- Nested long text with line clamp and load more button -->
                            <div v-else-if="isLongText(item[header])">
                              <p
                                :ref="
                                  el =>
                                    checkIfTruncated(
                                      el,
                                      `${key}-${header}-${index}`
                                    )
                                "
                                class="text-sm"
                                :class="{
                                  'line-clamp-3': !isTextExpanded(
                                    key,
                                    `${header}-${index}`
                                  ),
                                }"
                              >
                                {{ item[header] }}
                              </p>
                              <button
                                v-if="
                                  isTextTruncated(key, `${header}-${index}`) ||
                                  isTextExpanded(key, `${header}-${index}`)
                                "
                                type="button"
                                class="mt-1 text-xs text-blue-600 hover:underline"
                                @click="
                                  toggleTextExpansion(key, `${header}-${index}`)
                                "
                              >
                                {{
                                  isTextExpanded(key, `${header}-${index}`)
                                    ? 'Show less'
                                    : 'Load more'
                                }}
                              </button>
                            </div>

                            <!-- Nested array of strings with CsTag -->
                            <div
                              v-else-if="isArrayOfStrings(item[header])"
                              class="flex flex-wrap gap-1"
                            >
                              <CsTag
                                v-for="(str, idx) in item[header]"
                                :key="idx"
                                :is-closable="false"
                              >
                                {{ str }}
                              </CsTag>
                            </div>

                            <!-- Nested array of primitives -->
                            <div v-else-if="isArrayOfPrimitives(item[header])">
                              {{ item[header].join(', ') }}
                            </div>

                            <!-- Nested primitive -->
                            <div v-else-if="isPrimitive(item[header])">
                              {{ formatPrimitive(item[header]) }}
                            </div>

                            <!-- Nested object or complex structure -->
                            <div v-else-if="item[header]">
                              <CsAiActivityJsonViewer :data="item[header]" />
                            </div>
                          </CsTableCell>
                        </CsTableRow>
                      </CsTableBody>
                    </CsTable>
                  </div>
                </CsTableContainer>
              </div>

              <!-- Nested object (recursive render) -->
              <div v-else-if="isObject(value)">
                <CsAiActivityJsonViewer :data="value" />
              </div>

              <!-- Fallback for empty arrays or other edge cases -->
              <div v-else>
                {{ JSON.stringify(value) }}
              </div>
            </CsTableCell>
          </CsTableRow>
        </CsTableBody>
      </CsTable>
    </div>
  </CsTableContainer>
</template>
